import { useMemo } from "react";
import type { Control } from "react-hook-form";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import useTheme from "@mui/material/styles/useTheme";

import { neutral } from "app/themes/colors";
import CheckboxFormField from "components/checkbox-form-field";
import { getScribeModalPopoverZIndex } from "features/medical-scribe/medical-scribe-constants";

const COPY_BUTTON_SX = { border: "1px solid", borderColor: "divider" };

export type InsertScribeFormValues = {
  needReplace: boolean;
  isCollapsed: boolean;
};

type InsertScribeActionsProps = {
  control: Control<InsertScribeFormValues>;
  isMobile: boolean;
  isTablet: boolean;
  /** Without an editor the button copies, so there is nothing to replace or copy separately. */
  hasEditor: boolean;
  label: string;
  disabled: boolean;
  onSubmit: () => void;
  onCopyAndClose: () => void;
};

/** The footer: Replace existing content, the Insert (or Copy) button, and Copy text and close. */
export default function InsertScribeActions({
  control,
  isMobile,
  isTablet,
  hasEditor,
  label,
  disabled,
  onSubmit,
  onCopyAndClose
}: InsertScribeActionsProps) {
  const { zIndex } = useTheme();

  const sx = useMemo(
    () => ({
      actions: {
        background: neutral[50],
        borderTop: `solid 1px ${neutral[200]}`,
        px: isMobile ? 1 : 3,
        py: isTablet ? 0 : 2,
        pb: 1,
        pr: 0,
        mt: "auto",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        gap: isMobile ? 1 : 0
      },
      replaceLabel: {
        mr: isMobile ? 0 : 2,
        mb: isMobile ? -1 : 0,
        alignSelf: isMobile ? "flex-start" : "center"
      },
      submitButton: {
        ml: isMobile ? 0 : 2,
        mr: isMobile ? 0 : 2,
        width: isMobile ? "calc(100% - 16px)" : "auto",
        minHeight: isTablet ? "44px" : "auto"
      }
    }),
    [isMobile, isTablet]
  );

  const tooltipPopperProps = useMemo(
    () => ({ style: { zIndex: getScribeModalPopoverZIndex(zIndex.snackbar) } }),
    [zIndex.snackbar]
  );

  return (
    <DialogActions sx={sx.actions}>
      {hasEditor && (
        <FormControlLabel
          label="Replace existing content"
          sx={sx.replaceLabel}
          control={
            <CheckboxFormField
              name="needReplace"
              control={control}
              testId="need-replace"
            />
          }
        />
      )}
      <Button
        disabled={disabled}
        variant="contained"
        sx={sx.submitButton}
        onClick={onSubmit}
        startIcon={hasEditor ? <ContentPasteGoIcon /> : <ContentCopyIcon />}
      >
        {label}
      </Button>
      {hasEditor && (
        <Tooltip
          title="Copy text and close"
          arrow
          disableInteractive
          PopperProps={tooltipPopperProps}
        >
          <IconButton
            onClick={onCopyAndClose}
            color="primary"
            sx={COPY_BUTTON_SX}
          >
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      )}
    </DialogActions>
  );
}
